import React, { useState, useEffect } from "react";
import { X, Bot, CheckCircle, ArrowRight, FileText, Download } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import axios from "axios";
import { API, getContentSource } from "@/lib/constants";
import { hubspotContext } from "@/lib/hubspot";

const SESSION_KEY = "dmn_popup_session_shown";
const SWIPEFILE_KEY = "dmn_swipefile_100hooks";

const SUPPRESSED_PREFIXES = [
  "/contact",
  "/free-ai-visibility-audit",
  "/thank-you",
  "/admin",
  "/case-studies",
  "/checklists",
  "/quote",
  "/payment",
  "/privacy",
  "/terms",
  "/blog/social-media-hooks-that-stop-the-scroll",
];

const HOOKS_KEYWORDS = [
  "social-media", "hooks", "instagram", "tiktok", "facebook", "linkedin",
  "pinterest", "youtube", "content-marketing", "email-marketing",
  "copywriting", "video-marketing", "rumble",
];

// Decide which opt-in (if any) is relevant to the page being read
export const getPopupVariant = (pathname) => {
  const p = (pathname || "/").replace(/\/$/, "") || "/";
  if (SUPPRESSED_PREFIXES.some((prefix) => p.startsWith(prefix))) return null;
  if (p === "/services/social-media" || p === "/services/content-marketing" || p === "/video-marketing") return "hooks";
  if (p.startsWith("/blog/")) {
    const slug = p.replace("/blog/", "");
    return HOOKS_KEYWORDS.some((k) => slug.includes(k)) ? "hooks" : "mockup";
  }
  return "mockup";
};

const MockupForm = ({ onClose }) => {
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", website: "" });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/mockup-request`, {
        firstName: form.name,
        email: form.email,
        websiteUrl: form.website,
        source: "blog_aeo_geo_popup",
        lead_magnet: "free_aeo_geo_mockup",
        ...hubspotContext(),
      });
      onClose();
      navigate("/thank-you/lead?from=mockup");
    } catch {
      setSubmitted(true);
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="text-center py-8">
        <div className="w-16 h-16 bg-purple-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
          <CheckCircle className="w-8 h-8 text-purple-400" />
        </div>
        <h3 className="text-xl font-bold text-white mb-2">Mockup Request Received!</h3>
        <p className="text-gray-400 text-sm mb-4">Our team will analyze your website and send a customized AEO/GEO readiness mockup within 48-72 hours.</p>
        <Button onClick={onClose} variant="outline" className="border-purple-500/30 text-purple-400 hover:bg-purple-500/10">
          Continue Reading
        </Button>
      </div>
    );
  }

  return (
    <>
      <div className="text-center mb-6">
        <div className="inline-flex items-center gap-2 px-4 py-2 bg-purple-500/10 border border-purple-500/20 rounded-full text-sm mb-4">
          <Bot className="w-4 h-4 text-purple-400" />
          <span className="text-purple-300 font-medium">Free AEO/GEO Mockup</span>
        </div>
        <h2 className="text-2xl font-bold text-white mb-2">
          Is Your Website <span className="text-purple-400">AI-Ready?</span>
        </h2>
        <p className="text-gray-400 text-sm">
          Get a free mockup showing how your site could appear in ChatGPT, Perplexity, and Google AI Overviews.
        </p>
      </div>

      <div className="flex justify-center gap-6 mb-6 py-3 border-y border-white/10">
        <div className="text-center">
          <p className="text-lg font-bold text-white">84%</p>
          <p className="text-gray-500 text-xs">AI in Search</p>
        </div>
        <div className="text-center">
          <p className="text-lg font-bold text-white">100M+</p>
          <p className="text-gray-500 text-xs">ChatGPT Users</p>
        </div>
        <div className="text-center">
          <p className="text-lg font-bold text-white">40%</p>
          <p className="text-gray-500 text-xs">Gen Z Prefers AI</p>
        </div>
      </div>

      <form onSubmit={handleSubmit} id="aeo-geo-mockup-form" name="aeo-geo-mockup-form" className="space-y-3">
        <input type="hidden" name="content_source" value={getContentSource()} />
        <Input name="name" placeholder="Your Name *" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-white/5 border-white/10 text-white placeholder:text-gray-500" required data-testid="blog-popup-name" />
        <Input type="email" name="email" placeholder="Email *" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="bg-white/5 border-white/10 text-white placeholder:text-gray-500" required data-testid="blog-popup-email" />
        <Input type="url" name="website_url" placeholder="Your Website URL *" value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} className="bg-white/5 border-white/10 text-white placeholder:text-gray-500" required data-testid="blog-popup-website" />
        <Button type="submit" disabled={loading} className="w-full py-5 bg-gradient-to-r from-purple-500 to-cyan-500 hover:from-purple-600 hover:to-cyan-600 text-white font-semibold rounded-xl" data-testid="blog-popup-submit">
          {loading ? "Processing..." : <span className="flex items-center gap-2">Get My Free Mockup <ArrowRight className="w-4 h-4" /></span>}
        </Button>
      </form>
      <p className="text-xs text-gray-500 text-center mt-3">Delivered within 48-72 hours. No obligation.</p>
    </>
  );
};

const HooksForm = ({ onClose }) => {
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "" });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await axios.post(`${API}/lead-magnets/download`, {
        first_name: form.name,
        email: form.email,
        magnet: "100-hooks-swipe-file",
        source_page: window.location.pathname + " (popup)",
        hutk: hubspotContext().hutk,
      });
      localStorage.setItem(SWIPEFILE_KEY, res.data.download_url);
      onClose();
      navigate("/thank-you/swipe-file");
    } catch {
      toast.error("Something went wrong. Please check your email and try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className="text-center mb-6">
        <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#00E676]/10 border border-[#00E676]/20 rounded-full text-sm mb-4">
          <FileText className="w-4 h-4 text-[#00E676]" />
          <span className="text-[#00E676] font-medium">Free Swipe File</span>
        </div>
        <h2 className="text-2xl font-bold text-white mb-2">
          Steal Our <span className="text-[#00E676]">100 Hooks</span> That Stop the Scroll
        </h2>
        <p className="text-gray-400 text-sm">
          Fill-in-the-blank openers for any niche, sorted by goal, with the psychology behind why each one works.
        </p>
      </div>

      <div className="flex justify-center gap-6 mb-6 py-3 border-y border-white/10">
        <div className="text-center">
          <p className="text-lg font-bold text-white">10</p>
          <p className="text-gray-500 text-xs">Goals</p>
        </div>
        <div className="text-center">
          <p className="text-lg font-bold text-white">100</p>
          <p className="text-gray-500 text-xs">Templates</p>
        </div>
        <div className="text-center">
          <p className="text-lg font-bold text-white">1.7s</p>
          <p className="text-gray-500 text-xs">To Stop the Scroll</p>
        </div>
      </div>

      <form onSubmit={handleSubmit} id="hooks-swipe-file-form" name="hooks-swipe-file-form" className="space-y-3">
        <input type="hidden" name="content_source" value={getContentSource()} />
        <Input name="first_name" placeholder="First Name *" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-white/5 border-white/10 text-white placeholder:text-gray-500" required data-testid="hooks-popup-name" />
        <Input type="email" name="email" placeholder="Email *" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="bg-white/5 border-white/10 text-white placeholder:text-gray-500" required data-testid="hooks-popup-email" />
        <Button type="submit" disabled={loading} className="w-full py-5 btn-ninja font-semibold rounded-xl" data-testid="hooks-popup-submit">
          {loading ? "Unlocking..." : <span className="flex items-center gap-2">Get the 100-Hook Swipe File <Download className="w-4 h-4" /></span>}
        </Button>
      </form>
      <p className="text-xs text-gray-500 text-center mt-3">Instant download. No spam — ever.</p>
    </>
  );
};

const BlogMockupPopup = ({ isOpen, onClose, variant = "mockup" }) => {
  if (!isOpen) return null;

  const borderClass = variant === "hooks" ? "border-[#00E676]/20" : "border-purple-500/20";
  const glowClass = variant === "hooks"
    ? "bg-gradient-to-r from-[#00E676]/5 via-transparent to-cyan-500/5"
    : "bg-gradient-to-r from-purple-500/5 via-transparent to-cyan-500/5";

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4" data-testid={variant === "hooks" ? "hooks-swipefile-popup" : "blog-mockup-popup"}>
      <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={onClose} />
      <div className={`relative w-full max-w-md bg-gradient-to-br from-[#0a0a0a] via-[#111] to-[#0a0a0a] border ${borderClass} rounded-2xl overflow-hidden shadow-2xl`}>
        <button onClick={onClose} className="absolute top-4 right-4 p-2 text-gray-400 hover:text-white hover:bg-white/10 rounded-lg transition-colors z-10" data-testid="blog-popup-close">
          <X className="w-5 h-5" />
        </button>
        <div className={`absolute inset-0 ${glowClass}`} />
        <div className="relative p-6 md:p-8">
          {variant === "hooks" ? <HooksForm onClose={onClose} /> : <MockupForm onClose={onClose} />}
        </div>
      </div>
    </div>
  );
};

// Frequency rules: each variant shows ONCE ever (localStorage),
// and at most ONE auto-popup per browsing session (sessionStorage).
export const useBlogPopup = (options = {}) => {
  const { delay = 45000, scrollThreshold = 60, variant = null } = options;
  const [show, setShow] = useState(false);

  useEffect(() => {
    if (!variant) return;
    if (localStorage.getItem(`dmn_popup_shown_${variant}`)) return;
    if (sessionStorage.getItem(SESSION_KEY)) return;
    if (variant === "hooks" && localStorage.getItem(SWIPEFILE_KEY)) return;

    let triggered = false;
    const trigger = () => {
      if (triggered) return;
      // Never fire over an already-open lead modal/dialog
      if (document.querySelector('[role="dialog"], [data-lead-modal]')) return;
      triggered = true;
      setShow(true);
      localStorage.setItem(`dmn_popup_shown_${variant}`, "true");
      sessionStorage.setItem(SESSION_KEY, "true");
    };

    const timer = setTimeout(trigger, delay);
    const onScroll = () => {
      if (window.location.hash) return; // don't fire over anchor deep-links
      const h = document.documentElement.scrollHeight - window.innerHeight;
      const pct = h > 0 ? (window.scrollY / h) * 100 : 0;
      if (pct >= scrollThreshold) trigger();
    };
    window.addEventListener("scroll", onScroll);
    return () => { clearTimeout(timer); window.removeEventListener("scroll", onScroll); };
  }, [variant, delay, scrollThreshold]);

  return [show, setShow];
};

export default BlogMockupPopup;
